import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  VStack,
  HStack,
  Spinner,
  Tag,
  TagLabel,
  TagCloseButton,
  SimpleGrid,
  Heading,
  Divider,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Flex,
  InputGroup,
  InputLeftElement,
  Tooltip,
  Badge,
} from "@chakra-ui/react";
import { AddIcon, EditIcon, DeleteIcon, SearchIcon, InfoIcon } from "@chakra-ui/icons";
import { FiPlus, FiList } from "react-icons/fi";

import * as requests from "../../../api/requests";

const DEFAULT_FIELD_TYPE = "text";
const ENABLE_SECTOR_DELETE = false;

function SectorManager() {
  const [sectors, setSectors] = useState([]);
  const [sectorName, setSectorName] = useState("");
  const [sectorCode, setSectorCode] = useState("");
  const [mandatoryFields, setMandatoryFields] = useState([]);
  const [optionalFields, setOptionalFields] = useState([]);
  const [newField, setNewField] = useState("");
  const [editingSectorCode, setEditingSectorCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const fetchSectors = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await requests.getSectorTemplates();
      const body = await res.json();
      if (!res.ok) {
        setError(body.errmsg || "Failed to load sectors");
        setSectors([]);
        return;
      }
      setSectors(Array.isArray(body.sectors) ? body.sectors : []);
    } catch (err) {
      setError("Failed to load sectors");
      setSectors([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSectors();
  }, []);

  const sectorCodesInUse = useMemo(
    () => new Set(sectors.map((sector) => sector.sectorCode)),
    [sectors],
  );

  const filteredSectors = useMemo(() => {
    if (!searchQuery) return sectors;
    return sectors.filter(
      (s) =>
        s.sectorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.sectorCode.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [sectors, searchQuery]);

  const addField = () => {
    const trimmed = newField.trim();
    if (!trimmed) return;

    const fieldName = trimmed.toLowerCase().replace(/\s+/g, "_");
    if (mandatoryFields.some((field) => field.fieldName === fieldName)) {
      toast({
        title: "Field already exists",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    const fieldConfig = {
      fieldName,
      displayName: trimmed,
      fieldType: DEFAULT_FIELD_TYPE,
      isRequired: true,
      description: "",
    };

    setMandatoryFields([...mandatoryFields, fieldConfig]);
    setNewField("");
  };

  const removeField = (fieldName) => {
    setMandatoryFields(mandatoryFields.filter((f) => f.fieldName !== fieldName));
  };

  const handleSave = async () => {
    if (!sectorName.trim()) {
      toast({ title: "Sector name is required", status: "error" });
      return;
    }

    const normalizedSectorCode = sectorCode.trim().toUpperCase();
    if (!normalizedSectorCode) {
      toast({ title: "Sector code is required", status: "error" });
      return;
    }

    if (!mandatoryFields.length) {
      toast({ title: "At least one mandatory field is required", status: "error" });
      return;
    }

    if (!editingSectorCode && sectorCodesInUse.has(normalizedSectorCode)) {
      toast({ title: "Sector code already exists", status: "error" });
      return;
    }

    const payload = {
      sectorName: sectorName.trim(),
      sectorCode: normalizedSectorCode,
      mandatoryFields,
      optionalFields,
    };

    try {
      setSaving(true);
      let res;
      if (editingSectorCode) {
        res = await requests.updateSectorTemplate(editingSectorCode, payload);
      } else {
        res = await requests.createSectorTemplate(payload);
      }

      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.errmsg || "Failed to save sector");
      }

      toast({
        title: editingSectorCode ? "Sector updated" : "Sector created",
        status: "success",
        duration: 3000,
      });

      await fetchSectors();
      handleClose();
    } catch (err) {
      toast({
        title: "Operation failed",
        description: err.message,
        status: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (sector) => {
    setEditingSectorCode(sector.sectorCode);
    setSectorName(sector.sectorName || "");
    setSectorCode(sector.sectorCode || "");
    setMandatoryFields(sector.mandatoryFields || []);
    setOptionalFields(sector.optionalFields || []);
    onOpen();
  };

  const handleDelete = async (id) => {
    if (!ENABLE_SECTOR_DELETE) {
      toast({
        title: "Deletion disabled",
        description: "Delete functionality is currently disabled by system policy.",
        status: "info",
      });
      return;
    }

    try {
      setSaving(true);
      const res = await requests.deleteSectorTemplate(id);
      if (!res.ok) {
        const body = await res.json();
        throw new Error(body.errmsg || "Failed to delete sector");
      }
      toast({ title: "Sector deleted", status: "success" });
      await fetchSectors();
    } catch (err) {
      toast({ title: "Deletion failed", description: err.message, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setEditingSectorCode(null);
    setSectorName("");
    setSectorCode("");
    setMandatoryFields([]);
    setOptionalFields([]);
    setNewField("");
    onClose();
  };

  return (
    <Box w="100%" py={4}>
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "start", md: "center" }}
        mb={8}
        gap={4}
      >
        <VStack align="start" spacing={1}>
          <Heading size="lg" fontWeight="extrabold" color="gray.700">
            Sector Registry
          </Heading>
          <Text color="gray.500" fontSize="sm">
            Manage industry-specific certificate templates and mandatory fields
          </Text>
        </VStack>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          size="lg"
          onClick={onOpen}
          shadow="md"
          _hover={{ transform: "translateY(-2px)", shadow: "lg" }}
          transition="all 0.2s"
        >
          Add New Sector
        </Button>
      </Flex>

      <Box mb={8}>
        <InputGroup size="lg" maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search sectors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="white"
            borderRadius="full"
            shadow="sm"
          />
        </InputGroup>
      </Box>

      {loading ? (
        <Flex justify="center" align="center" py={20}>
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color="gray.500" fontWeight="medium">
              Loading sectors registry...
            </Text>
          </VStack>
        </Flex>
      ) : error ? (
        <Box textAlign="center" py={20} bg="red.50" borderRadius="xl">
          <Text color="red.500" fontSize="lg">
            {error}
          </Text>
          <Button mt={4} onClick={fetchSectors} variant="outline" colorScheme="red">
            Retry
          </Button>
        </Box>
      ) : filteredSectors.length === 0 ? (
        <Box
          textAlign="center"
          py={20}
          bg="gray.50"
          borderRadius="xl"
          border="2px dashed"
          borderColor="gray.200"
        >
          <VStack spacing={4}>
            <FiList size={48} color="#CBD5E0" />
            <Text color="gray.500" fontSize="lg" fontWeight="medium">
              {searchQuery ? "No sectors matching your search" : "No sectors found in the registry"}
            </Text>
            {!searchQuery && (
              <Button onClick={onOpen} colorScheme="blue" variant="ghost">
                Create your first sector template
              </Button>
            )}
          </VStack>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {filteredSectors.map((sector) => (
            <Box
              key={sector.sectorCode}
              bg="white"
              p={6}
              borderRadius="2xl"
              shadow="sm"
              border="1px solid"
              borderColor="gray.100"
              _hover={{ shadow: "xl", borderColor: "blue.200", transform: "translateY(-4px)" }}
              transition="all 0.3s cubic-bezier(.08,.52,.52,1)"
              position="relative"
              overflow="hidden"
            >
              <Box position="absolute" top={0} left={0} w="4px" h="100%" bg="blue.500" />

              <Flex justify="space-between" align="start" mb={4}>
                <VStack align="start" spacing={0}>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color="blue.500"
                    textTransform="uppercase"
                    letterSpacing="wider"
                  >
                    {sector.sectorCode}
                  </Text>
                  <Heading size="md" color="gray.800" noOfLines={1}>
                    {sector.sectorName}
                  </Heading>
                </VStack>
                <HStack spacing={1}>
                  <Tooltip label="Edit Sector">
                    <IconButton
                      size="sm"
                      icon={<EditIcon />}
                      aria-label="Edit"
                      variant="ghost"
                      color="gray.400"
                      _hover={{ color: "blue.500", bg: "blue.50" }}
                      onClick={() => handleEdit(sector)}
                    />
                  </Tooltip>
                  {ENABLE_SECTOR_DELETE && (
                    <Tooltip label="Delete Sector">
                      <IconButton
                        size="sm"
                        icon={<DeleteIcon />}
                        aria-label="Delete"
                        variant="ghost"
                        color="red.400"
                        _hover={{ color: "red.500", bg: "red.50" }}
                        onClick={() => handleDelete(sector.sectorCode)}
                        isLoading={saving}
                      />
                    </Tooltip>
                  )}
                </HStack>
              </Flex>

              <Divider mb={4} />

              <VStack align="start" spacing={3}>
                <Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">
                  Mandatory Fields ({sector.mandatoryFields?.length || 0})
                </Text>
                <Flex flexWrap="wrap" gap={2}>
                  {(sector.mandatoryFields || []).map((field) => (
                    <Badge
                      key={field.fieldName}
                      colorScheme="blue"
                      variant="subtle"
                      px={2}
                      py={1}
                      borderRadius="md"
                      fontSize="xs"
                    >
                      {field.displayName || field.fieldName}
                    </Badge>
                  ))}
                </Flex>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {/* Single Modal for Add/Edit */}
      <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl">
          <ModalHeader borderBottomWidth="1px">
            <VStack align="start" spacing={0}>
              <Text fontSize="xs" color="blue.500" fontWeight="bold">
                {editingSectorCode ? "TEMPLATE CONFIGURATION" : "NEW REGISTRY ENTRY"}
              </Text>
              <Text fontSize="xl">
                {editingSectorCode ? `Edit ${sectorName}` : "Create New Sector Template"}
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={6}>
              <FormControl isRequired>
                <FormLabel fontWeight="bold">Sector Name</FormLabel>
                <Input
                  placeholder="e.g. Healthcare, Supply Chain"
                  value={sectorName}
                  onChange={(e) => setSectorName(e.target.value)}
                  size="lg"
                  borderRadius="xl"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontWeight="bold">Sector Code</FormLabel>
                <Input
                  placeholder="e.g. MED, LOG"
                  value={sectorCode}
                  onChange={(e) => setSectorCode(e.target.value.toUpperCase())}
                  disabled={Boolean(editingSectorCode)}
                  size="lg"
                  borderRadius="xl"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Unique identifier used for template mapping
                </Text>
              </FormControl>

              <Box
                w="100%"
                p={4}
                bg="blue.50"
                borderRadius="xl"
                border="1px solid"
                borderColor="blue.100"
              >
                <FormControl>
                  <FormLabel fontWeight="bold" display="flex" alignItems="center">
                    Define Mandatory Fields
                    <Tooltip label="These fields will be required for all certificates issued in this sector">
                      <Box ml={2} color="blue.400">
                        <InfoIcon size={12} />
                      </Box>
                    </Tooltip>
                  </FormLabel>
                  <HStack>
                    <Input
                      placeholder="e.g. Batch ID, Manufacturer"
                      value={newField}
                      onChange={(e) => setNewField(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addField()}
                      bg="white"
                      borderRadius="xl"
                    />
                    <IconButton
                      colorScheme="blue"
                      icon={<AddIcon />}
                      onClick={addField}
                      aria-label="Add Field"
                      borderRadius="xl"
                    />
                  </HStack>
                </FormControl>

                <Flex flexWrap="wrap" mt={4} gap={2}>
                  {mandatoryFields.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" fontStyle="italic">
                      No fields defined yet
                    </Text>
                  ) : (
                    mandatoryFields.map((field) => (
                      <Tag
                        size="lg"
                        colorScheme="blue"
                        key={field.fieldName}
                        borderRadius="full"
                        px={3}
                      >
                        <TagLabel fontSize="sm" fontWeight="medium">
                          {field.displayName || field.fieldName}
                        </TagLabel>
                        <TagCloseButton onClick={() => removeField(field.fieldName)} />
                      </Tag>
                    ))
                  )}
                </Flex>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter borderTopWidth="1px">
            <Button variant="ghost" mr={3} onClick={handleClose} borderRadius="xl">
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isLoading={saving}
              loadingText="Saving..."
              borderRadius="xl"
              px={8}
            >
              {editingSectorCode ? "Update Template" : "Register Sector"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default SectorManager;
